import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Heading,
  SimpleGrid,
  Switch as ChakraSwitch,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, FieldError, SubmitHandler, useForm } from 'react-hook-form'
import { useHistory } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import * as yup from 'yup'

import { Input } from '../../../components/Form/Input'
import { PaymentMethod } from '../../../Interfaces/paymentMethod'
import { api } from '../../../services/api'

type FormValues = {
  name: string
  code: string
  description?: string
  is_active: boolean
  requires_approval: boolean
  fee_percentage?: number
  fee_fixed?: number
  processing_days?: number
}

const schema = yup.object().shape({
  name: yup.string().required('Nome é obrigatório'),
  code: yup.string().required('Código é obrigatório'),
  description: yup.string().nullable(),
  is_active: yup.boolean().default(true),
  requires_approval: yup.boolean().default(false),
  fee_percentage: yup
    .number()
    .transform((value) => (Number.isNaN(value) ? 0 : value))
    .min(0, 'A taxa percentual não pode ser negativa'),
  fee_fixed: yup
    .number()
    .transform((value) => (Number.isNaN(value) ? 0 : value))
    .min(0, 'A taxa fixa não pode ser negativa'),
  processing_days: yup
    .number()
    .transform((value) => (Number.isNaN(value) ? 0 : value))
    .min(0, 'Os dias de processamento não podem ser negativos'),
})

export default function PaymentMethodForm({ match }: any) {
  const history = useHistory()
  const toast = useToast()
  const queryClient = useQueryClient()

  const { id } = match.params
  const isEditMode = Boolean(id)

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      is_active: true,
      requires_approval: false,
    },
  })

  useQuery(
    ['payment-method', id],
    async () => {
      const { data } = await api.get(`payment-methods/${id}`)
      return data?.data as PaymentMethod
    },
    {
      enabled: isEditMode,
      onSuccess: (paymentMethod) => {
        if (!paymentMethod) return

        reset({
          name: paymentMethod.name,
          code: paymentMethod.code,
          description: paymentMethod.description || '',
          is_active: paymentMethod.is_active,
          requires_approval: paymentMethod.requires_approval,
          fee_percentage: paymentMethod.fee_percentage,
          fee_fixed: paymentMethod.fee_fixed,
          processing_days: paymentMethod.processing_days,
        })
      },
    },
  )

  const mutation = useMutation(
    async (values: FormValues) => {
      const payload = {
        ...values,
        description: values.description?.trim() || undefined,
        fee_percentage: Number(values.fee_percentage) || 0,
        fee_fixed: Number(values.fee_fixed) || 0,
        processing_days: Number(values.processing_days) || 0,
      }

      if (isEditMode) {
        await api.put(`payment-methods/${id}`, payload)
      } else {
        await api.post('payment-methods', payload)
      }
    },
    {
      onSuccess: async () => {
        toast({
          description: `Forma de pagamento ${isEditMode ? 'atualizada' : 'criada'} com sucesso`,
          status: 'success',
        })
        await queryClient.invalidateQueries('payment-methods')
        history.push('/payment-methods')
      },
      onError: ({ response }) => {
        const errors = response?.data?.errors || []
        if (errors.length) {
          errors.forEach((err) => toast({ description: err.message, status: 'error' }))
        } else {
          toast({ description: 'Não foi possível salvar a forma de pagamento', status: 'error' })
        }
      },
    },
  )

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    await mutation.mutateAsync(values)
  }

  return (
    <Box as="form" flex="1" borderRadius="8" bg="gray.800" p="8" onSubmit={handleSubmit(onSubmit)}>
      <Heading size="lg" fontWeight="normal">
        {isEditMode ? 'Editar Forma de Pagamento' : 'Nova Forma de Pagamento'}
      </Heading>

      <Divider my="6" borderColor="gray.700" />

      <VStack spacing="8">
        <SimpleGrid minChildWidth="240px" spacing="8" w="100%">
          <Input label="Nome" {...register('name')} error={errors.name as FieldError} />
          <Input label="Código" {...register('code')} error={errors.code as FieldError} />
          <Input label="Descrição" {...register('description')} error={errors.description as FieldError} />
        </SimpleGrid>

        <SimpleGrid minChildWidth="240px" spacing="8" w="100%">
          <Input
            label="Taxa percentual ( % )"
            type="number"
            step="0.01"
            {...register('fee_percentage')}
            error={errors.fee_percentage as FieldError}
          />
          <Input
            label="Taxa fixa (R$)"
            type="number"
            step="0.01"
            {...register('fee_fixed')}
            error={errors.fee_fixed as FieldError}
          />
          <Input
            label="Dias de processamento"
            type="number"
            {...register('processing_days')}
            error={errors.processing_days as FieldError}
          />
        </SimpleGrid>

        <SimpleGrid minChildWidth="240px" spacing="8" w="100%">
          <Controller
            name="is_active"
            control={control}
            render={({ field }) => (
              <HStack align="center" spacing={3}>
                <ChakraSwitch
                  colorScheme="pink"
                  isChecked={!!field.value}
                  onChange={(event) => field.onChange(event.target.checked)}
                />
                <Text>Ativo</Text>
              </HStack>
            )}
          />

          <Controller
            name="requires_approval"
            control={control}
            render={({ field }) => (
              <HStack align="center" spacing={3}>
                <ChakraSwitch
                  colorScheme="pink"
                  isChecked={!!field.value}
                  onChange={(event) => field.onChange(event.target.checked)}
                />
                <Text>Requer aprovação</Text>
              </HStack>
            )}
          />
        </SimpleGrid>
      </VStack>

      <Flex mt="8" justify="flex-end">
        <HStack spacing="4">
          <Button variant="ghost" onClick={() => history.push('/payment-methods')}>
            Cancelar
          </Button>
          <Button type="submit" colorScheme="pink" isLoading={isSubmitting || mutation.isLoading}>
            Salvar
          </Button>
        </HStack>
      </Flex>
    </Box>
  )
}
